import type { ApiClient } from './fetsClient';
import { createCourseExportClient } from '../pages/course-export/rest-api/courseExport';
import { assignTagFactory } from './endpoints/assignTag';
import { assignTagToCourseExportFactory } from './endpoints/assignTagToCourseExport';
import { checkExportProgressFactory } from './endpoints/checkExportProgress';
import { createProjectFactory } from './endpoints/createProject';
import { createProjectFromTemplateFactory } from './endpoints/createProjectFromTemplate';
import { createTagFactory } from './endpoints/createTag';
import { deleteProjectFactory } from './endpoints/deleteProject';
import { deleteSceneFactory } from './endpoints/deleteScene';
import { deleteTagFactory } from './endpoints/deleteTag';
import { downgradeSubscriptionFactory } from './endpoints/downgradeSubscription';
import { duplicateProjectFactory } from './endpoints/duplicateProject';
import { editTeamMemberFactory } from './endpoints/editTeamMember';
import { exportProjectFactory } from './endpoints/exportProject';
import { generateCheckoutSessionFactory } from './endpoints/generateCheckoutSession';
import { getMeFactory } from './endpoints/getMe';
import { getMeOptionalFactory } from './endpoints/getMeOptional';
import { getOnboardingFactory } from './endpoints/getOnboarding';
import { getProjectQuizesFactory } from './endpoints/getProjectQuizes';
import { getProjectFactory } from './endpoints/getProject';
import { getProjectsByTourIdsFactory } from './endpoints/getProjectsByTourIds';
import { getSceneProcessingStatusFactory } from './endpoints/getSceneProcessingStatus';
import { getSubscriptionPageFactory } from './endpoints/getSubscriptionPage';
import { getSubscriptionPlanFactory } from './endpoints/getSubscriptionPlan';
import { getTagsFactory } from './endpoints/getTags';
import { inviteTeamMemberFactory } from './endpoints/inviteTeamMember';
import { listProjectsFactory } from './endpoints/listProjects';
import { listScenesFactory } from './endpoints/listScenes';
import { listTeamMembersFactory } from './endpoints/listTeamMembers';
import { listTemplatesFactory } from './endpoints/listTemplates';
import { modifySeatsFactory } from './endpoints/modifySeats';
import { prepareSceneFactory } from './endpoints/prepareScene';
import { prorateSeatPriceFactory } from './endpoints/prorateSeatPrice';
import { registerNewUserFactory } from './endpoints/registerNewUser';
import { removeTeamMemberFactory } from './endpoints/removeTeamMember';
import { renameOrganizationFactory } from './endpoints/renameOrganization';
import { renameProjectFactory } from './endpoints/renameProject';
import { renameSceneFactory } from './endpoints/renameScene';
import { resetSceneThumbnailFactory } from './endpoints/resetSceneThumbnail';
import { setProjectPublicAvailabilityFactory } from './endpoints/setProjectPublicAvailability';
import { startProjectExportFactory } from './endpoints/startProjectExport';
import { updateOnboardingFactory } from './endpoints/updateOnboarding';
import { updateProjectFeedbackEmailFactory } from './endpoints/updateProjectFeedbackEmail';
import { updateProjectPropertiesFactory } from './endpoints/updateProjectProperties';
import { updateProjectWatermarkFactory } from './endpoints/updateProjectWatermark';
import { updateTagFactory } from './endpoints/updateTag';
import { uploadLogoFactory } from './endpoints/uploadLogo';
import { uploadSceneFactory } from './endpoints/uploadScene';
import { uploadSceneThumbnailFactory } from './endpoints/uploadSceneThumbnail';
import { uploadThumbnailFactory } from './endpoints/uploadThumbnail';
import { updateTrackingSettingsFactory } from './endpoints/updateTrackingSettings';
import { upgradeSubscriptionFactory } from './endpoints/upgradeSubscription';

const _createApiClient = (apiClient: ApiClient) => {
  const getMe = getMeFactory(apiClient);
  const getMeOptional = getMeOptionalFactory(apiClient);
  const getSubscriptionPlan = getSubscriptionPlanFactory(apiClient);
  const getSubscriptionPage = getSubscriptionPageFactory(apiClient);
  const prorateSeatPrice = prorateSeatPriceFactory(apiClient);
  const listProjects = listProjectsFactory(apiClient);
  const uploadThumbnail = uploadThumbnailFactory(apiClient);
  const createProject = createProjectFactory(apiClient);
  const createProjectFromTemplate = createProjectFromTemplateFactory(apiClient);
  const duplicateProject = duplicateProjectFactory(apiClient);
  const deleteProject = deleteProjectFactory(apiClient);
  const updateProjectProperties = updateProjectPropertiesFactory(apiClient);
  const checkExportProgress = checkExportProgressFactory(apiClient);
  const getTags = getTagsFactory(apiClient);
  const createTag = createTagFactory(apiClient);
  const updateTag = updateTagFactory(apiClient);
  const deleteTag = deleteTagFactory(apiClient);
  const assignTag = assignTagFactory(apiClient);
  const assignTagToCourseExport = assignTagToCourseExportFactory(apiClient);
  const listScenes = listScenesFactory(apiClient);
  const listTemplates = listTemplatesFactory(apiClient);
  const prepareScene = prepareSceneFactory(apiClient);
  const updateProjectWatermark = updateProjectWatermarkFactory(apiClient);
  const updateProjectFeedbackEmail = updateProjectFeedbackEmailFactory(apiClient);
  const renameProject = renameProjectFactory(apiClient);
  const setProjectPublicAvailability = setProjectPublicAvailabilityFactory(apiClient);
  const exportProject = exportProjectFactory(apiClient);
  const deleteScene = deleteSceneFactory(apiClient);
  const renameScene = renameSceneFactory(apiClient);
  const updateOnboarding = updateOnboardingFactory(apiClient);
  const getOnboarding = getOnboardingFactory(apiClient);
  const inviteTeamMember = inviteTeamMemberFactory(apiClient);
  const editTeamMember = editTeamMemberFactory(apiClient);
  const removeTeamMember = removeTeamMemberFactory(apiClient);
  const listTeamMembers = listTeamMembersFactory(apiClient);
  const getProject = getProjectFactory(apiClient);
  const getProjectsByTourIds = getProjectsByTourIdsFactory(apiClient);
  const getSceneProcessingStatus = getSceneProcessingStatusFactory(apiClient);
  const getProjectQuizes = getProjectQuizesFactory(apiClient);
  const uploadLogo = uploadLogoFactory(apiClient);
  const uploadScene = uploadSceneFactory(apiClient);
  const uploadSceneThumbnail = uploadSceneThumbnailFactory(apiClient);
  const resetSceneThumbnail = resetSceneThumbnailFactory(apiClient);
  const downgradeSubscription = downgradeSubscriptionFactory(apiClient);
  const generateCheckoutSession = generateCheckoutSessionFactory(apiClient);
  const modifySeats = modifySeatsFactory(apiClient);
  const upgradeSubscription = upgradeSubscriptionFactory(apiClient);
  const registerNewUser = registerNewUserFactory(apiClient);
  const renameOrganization = renameOrganizationFactory(apiClient);
  const updateTrackingSettings = updateTrackingSettingsFactory(apiClient);

  return {
    getMe,
    getMeOptional,
    getSubscriptionPlan,
    getSubscriptionPage,
    prorateSeatPrice,
    // projects
    listProjects,
    createProject,
    createProjectFromTemplate,
    uploadThumbnail,
    uploadLogo,
    duplicateProject,
    deleteProject,
    updateProjectProperties,
    updateProjectWatermark,
    updateProjectFeedbackEmail,
    renameProject,
    setProjectPublicAvailability,
    startProjectExport: startProjectExportFactory(apiClient),
    exportProject,
    checkExportProgress,
    getProject,
    getProjectsByTourIds,
    getProjectQuizes,
    // tags
    getTags,
    createTag,
    updateTag,
    deleteTag,
    setTagToProject: assignTag,
    setTagToCourseExport: assignTagToCourseExport,
    // scenes
    listScenes,
    listTemplates,
    prepareScene,
    deleteScene,
    renameScene,
    uploadScene,
    uploadSceneThumbnail,
    resetSceneThumbnail,
    getSceneProcessingStatus,
    // onboarding
    updateOnboarding,
    getOnboarding,
    // team
    inviteTeamMember,
    editTeamMember,
    removeTeamMember,
    listTeamMembers,
    // subscription
    downgradeSubscription,
    generateCheckoutSession,
    modifySeats,
    upgradeSubscription,
    // organization
    registerNewUser,
    renameOrganization,
    updateTrackingSettings,

    ...createCourseExportClient(apiClient),
  };
};

export type PortalApiClient = ReturnType<typeof _createApiClient>;
export const createPortalApiClient = (apiClient: ApiClient): PortalApiClient => {
  return _createApiClient(apiClient);
};
